import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styles from '../TradeFlowTrackingTab.module.css';
import { useFlowData } from '../hooks/useTradeFlowData';
import type { LightGBMDecisionItem, LightGBMDecisionsResponse } from '../../../services/lightgbmAdminApi';

interface LGBMSectionProps {
  dateFrom: string;
  dateTo: string;
  symbol: string;
}

interface LGBMDecision {
  id: string;
  symbol: string;
  score: number | null;
  threshold: number | null;
  passed: boolean | null;
  reason: string | null;
  stage: string | null;
  modelVersion: string | null;
  createdAt: string | null;
}

const LGBMSection: React.FC<LGBMSectionProps> = ({ dateFrom, dateTo, symbol }) => {
  const [lgbEnabled, setLgbEnabled] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [showPassed, setShowPassed] = useState<string>('all');

  useEffect(() => {
    let cancelled = false;
    async function checkEnabled() {
      try {
        const resp = await fetch('/api/admin/lightgbm/overview', { credentials: 'include' });
        if (resp.ok) {
          const data = await resp.json();
          const enabled = data?.enabled ?? false;
          if (!cancelled) setLgbEnabled(enabled);
        } else {
          if (!cancelled) setLgbEnabled(false);
        }
      } catch {
        if (!cancelled) setLgbEnabled(false);
      } finally {
        if (!cancelled) setSettingsLoaded(true);
      }
    }
    checkEnabled();
    return () => { cancelled = true; };
  }, []);

  const params = useMemo(() => ({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    symbol: symbol || undefined,
    passed: showPassed === 'all' ? undefined : showPassed === 'passed',
  }), [dateFrom, dateTo, symbol, showPassed]);

  const { data, total, page, setPage, isLoading, isError, error, totalPages } =
    useFlowData<LGBMDecision>({
      queryKeyBase: ['admin', 'trade-flow', 'lgbm'],
      fetchFn: async (p) => {
        const queryParams = new URLSearchParams();
        queryParams.set('page', String(p.page));
        queryParams.set('page_size', String(p.page_size));
        const pRecord = p as Record<string, unknown>;
        if (pRecord.date_from) queryParams.set('date_from', String(pRecord.date_from));
        if (pRecord.date_to) queryParams.set('date_to', String(pRecord.date_to));
        if (pRecord.symbol) queryParams.set('symbol', String(pRecord.symbol));
        if (pRecord.passed !== undefined) queryParams.set('passed', String(pRecord.passed));
        const resp = await fetch(
          `/api/admin/lightgbm/decisions?${queryParams.toString()}`,
          { credentials: 'include' },
        );
        if (!resp.ok) throw new Error('Failed to fetch LGBM decisions');
        const json = await resp.json() as { items: LGBMDecision[]; total: number };
        return {
          data: json.items || [],
          total: json.total || 0,
        };
      },
      params,
    });

  if (!settingsLoaded) {
    return (
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>LightGBM AI Filter</h2>
          <span className={styles.sectionBadge}>Tab 5</span>
        </div>
        <div className={styles.loadingState}>Checking LightGBM status...</div>
      </div>
    );
  }

  if (!lgbEnabled) {
    return (
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>LightGBM AI Filter</h2>
          <span className={styles.sectionBadge}>Tab 5</span>
        </div>
        <div className={styles.emptyState}>
          LightGBM is currently disabled. Enable it from the LightGBM Control Center page to see decisions here.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2>LightGBM AI Filter</h2>
        <span className={styles.sectionBadge}>Tab 5</span>
      </div>
      <p className={styles.sectionDescription}>
        LightGBM filter decisions showing which symbols passed or were rejected with their probability scores.
      </p>

      <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', alignItems: 'center' }}>
        <label style={{ fontSize: '14px', color: '#94a3b8' }}>Filter:</label>
        <select
          value={showPassed}
          onChange={(e) => setShowPassed(e.target.value)}
          style={{
            padding: '4px 8px',
            background: '#1e293b',
            color: '#e2e8f0',
            border: '1px solid #334155',
            borderRadius: '6px',
            fontSize: '13px',
          }}
        >
          <option value="all">All</option>
          <option value="passed">Passed</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {error && <div className={styles.errorBanner}>{String(error)}</div>}

      {isLoading ? (
        <div className={styles.loadingState}>Loading data...</div>
      ) : data.length === 0 ? (
        <div className={styles.emptyState}>No LightGBM decisions found.</div>
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Time</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Symbol</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px' }}>Score</th>
                  <th style={{ textAlign: 'right', padding: '8px 12px' }}>Threshold</th>
                  <th style={{ textAlign: 'center', padding: '8px 12px' }}>Result</th>
                  <th style={{ textAlign: 'left', padding: '8px 12px' }}>Reason</th>
                </tr>
              </thead>
              <tbody>
                {data.map((d) => {
                  const isPassed = d.passed === true;
                  const badgeColor = isPassed ? '#22c55e' : '#ef4444';
                  return (
                    <tr key={d.id}>
                      <td style={{ padding: '6px 12px', fontSize: '12px', color: '#94a3b8' }}>
                        {d.createdAt ? new Date(d.createdAt).toLocaleString() : '-'}
                      </td>
                      <td style={{ padding: '6px 12px', fontWeight: 600 }}>{d.symbol}</td>
                      <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>
                        {d.score != null ? d.score.toFixed(4) : '-'}
                      </td>
                      <td style={{ padding: '6px 12px', textAlign: 'right', fontFamily: 'monospace' }}>
                        {d.threshold != null ? d.threshold.toFixed(2) : '-'}
                      </td>
                      <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                        <span style={{
                          background: badgeColor + '20',
                          color: badgeColor,
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: 600,
                        }}>
                          {isPassed ? 'PASS' : 'REJECT'}
                        </span>
                      </td>
                      <td style={{ padding: '6px 12px', fontSize: '12px', color: '#94a3b8', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.reason || '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className={styles.pagination}>
            <button
              disabled={page <= 1}
              onClick={() => setPage(Math.max(1, page - 1))}
              className={styles.paginationButton}
            >
              Previous
            </button>
            <span className={styles.paginationInfo}>
              Page {page} of {totalPages} ({total} total)
            </span>
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              className={styles.paginationButton}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default LGBMSection;
